// Schedule pickup and Mark shipped (spec §3.8, Figures 22–23). The checks are `shipping.ts`'s:
// a blocked one is the big red message, a warning the orange "Are you sure?" (spec §3.7).

import { useEffect, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent, ReactNode, RefObject } from "react";
import {
  bolText,
  checkPickup,
  checkShipment,
  defaultCarrier,
  formatDay,
  formatShortDay,
  parseDateInput,
  parseTime,
  timeShown,
  typedTimeShown,
} from "@app/shared";
import type {
  CarrierPick,
  CarrierRow,
  Checked,
  LineSource,
  LineView,
  PickupRecord,
  RescheduleField,
  RescheduleValues,
} from "@app/shared";
import { useLineCarriers } from "./data";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useMessage } from "./message";
import { CarrierField, DateField } from "./pickers";
import type { CarrierSourceApi } from "./pickers";
import { refocusEditor } from "./refocus";
import type { PickupInput, ShipInput } from "./shipping-writer";
import { pickupText } from "./shipping-writer";
import { useDialogDrag } from "./dialog-drag";
import { DialogLines } from "./source-filter";

function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-1", className)}>
      <Label className="text-xs font-medium">{label}</Label>
      {children}
    </div>
  );
}

const units = (lines: readonly LineView[]) => lines.reduce((n, l) => n + l.qty, 0).toLocaleString("en-US");
const customers = (lines: readonly LineView[]) => [...new Set(lines.map((l) => l.customerName))].join(", ");
const earliestCancel = (lines: readonly LineView[]) =>
  lines
    .map((l) => l.cancel)
    .filter((d): d is string => !!d)
    .sort()[0];

/**
 * Enter in a field submits the form, as its main button does — not when the Carrier list takes it
 * (`CarrierField`), and not while a message is open over the dialog (it owns Enter then).
 */
const submitOnEnter = (submit: () => void) => (e: KeyboardEvent<HTMLElement>) => {
  const target = e.target as HTMLElement;
  if (e.key !== "Enter" || e.defaultPrevented || target.tagName !== "INPUT") return;
  e.preventDefault();
  submit();
};

/**
 * The field a dialog opens in: its text selected the first time it takes the focus, so typing
 * replaces the filled-in value instead of adding to it. A later click in it places the caret.
 */
function useSelectOnOpen() {
  const fresh = useRef(true);
  return (e: FocusEvent<HTMLInputElement>) => {
    if (!fresh.current) return;
    fresh.current = false;
    e.target.select();
  };
}

/** After "OK, I'll fix it": back in the field, its text selected (as on the Worklist). */
const refocus = (ref: RefObject<HTMLInputElement | null>) => refocusEditor(ref.current);

/**
 * Only Cancel, the X and Esc close a form: not a press outside it or the focus leaving it — the
 * big red message's own button is outside, and closing would lose what was typed.
 */
export const closesOnlyByHand = (onClose: () => void) => (open: boolean, details: { reason: string }) => {
  if (!open && details.reason !== "outside-press" && details.reason !== "focus-out") onClose();
};

/**
 * Runs the checks: the big red message for what is blocked — then back in the field it is about
 * (`fieldOf`) — and "Are you sure?" for each warning.
 */
async function passes(
  message: ReturnType<typeof useMessage>,
  checked: { errors: string[]; warnings: string[] },
  title: string,
  anyway: string,
  fieldOf: (error: string) => RefObject<HTMLInputElement | null>,
): Promise<boolean> {
  if (checked.errors.length) {
    await message.error(title, checked.errors[0], checked.errors.slice(1));
    refocus(fieldOf(checked.errors[0]!));
    return false;
  }
  if (!checked.warnings.length) return true;
  return message.confirm("Are you sure?", "Check these before you go on:", {
    lines: checked.warnings,
    confirmLabel: anyway,
  });
}

/** A typed day, as `YYYY-MM-DD`, or the big red message and back in its field. */
async function dayOf(
  message: ReturnType<typeof useMessage>,
  text: string,
  today: string,
  what: string,
  field: RefObject<HTMLInputElement | null>,
) {
  const parsed = parseDateInput(text, "appointmentDate", today);
  if (!parsed.ok || !parsed.value) {
    await message.error(`Can't use this ${what}`, parsed.ok ? `Type the ${what}, like 10/16` : parsed.message);
    refocus(field);
    return null;
  }
  return parsed.value;
}

/**
 * Reschedule (item 2): the same dialog, filled from the selected rows' pickup(s) — the whole
 * pickup(s), or a new pickup for the lines moved off. A field the pickups differ on starts empty:
 * left empty, each pickup keeps its own.
 */
export interface RescheduleProps {
  how: "whole" | "move";
  prefill: RescheduleValues;
  several: readonly RescheduleField[];
  /** The pickups' checks with these values (`checkReschedule`). */
  check: (typed: RescheduleValues) => Checked;
  onReschedule: (typed: RescheduleValues) => void;
}

const SEVERAL = "Several values — leave empty to keep each";

/** A saved carrier as the picker holds it: picked from the list (with its code), or none — a typed one is "was: …". */
const pickOf = (
  p: { carrier?: string | null; carrierCode?: string | null; carrierSource?: string | null } | undefined,
) =>
  p?.carrier && p.carrierCode
    ? { carrier: p.carrier, carrierCode: p.carrierCode, carrierSource: (p.carrierSource || "A2000") as LineSource }
    : null;

/**
 * What a form saves for its carrier field: the pick; left as it was, a carrier typed before stays
 * (the picker shows "was: …" and never re-saves it as a pick); cleared, none.
 */
const carrierOut = (pick: CarrierPick | null, touched: boolean, was: string) =>
  pick
    ? { carrier: pick.carrier, carrierCode: pick.carrierCode, carrierSource: pick.carrierSource }
    : { carrier: touched ? "" : was, carrierCode: "", carrierSource: "" };

/** Schedule pickup: a new pickup, or add the lines to one already scheduled (the same truck). */
export function SchedulePickupDialog({
  lines,
  pickups: allPickups,
  carriers,
  carrierRows,
  date: cellDate,
  today,
  onClose,
  onSchedule,
  reschedule,
}: {
  lines: LineView[];
  /** Scheduled pickups from today on, with how many lines each holds. */
  pickups: readonly { pickup: PickupRecord; rows: number }[];
  /** The carriers of A2000 and Deposco (item 8). */
  carriers: CarrierSourceApi;
  /** Every row of the carriers view, placeholders too: the names of "Lines have: …". */
  carrierRows: readonly CarrierRow[];
  /** Opened from the Worklist's Appointment date cell: the date (YYYY-MM-DD), and only that day's pickups to join. */
  date?: string;
  today: string;
  onClose: () => void;
  onSchedule: (target: { input: PickupInput } | { pickup: PickupRecord; rows: number }) => void;
  /** Reschedule pickup instead of Schedule pickup. */
  reschedule?: RescheduleProps;
}) {
  const drag = useDialogDrag();
  const message = useMessage();
  const [mode, setMode] = useState<"new" | "existing">("new");
  const start = reschedule?.prefill;
  const startDate = start?.pickupDate || cellDate;
  const [date, setDate] = useState(startDate ? formatDay(startDate) : "");
  const [time, setTime] = useState(timeShown(start?.appTime));
  // Reschedule keeps the pickup's carrier; a new pickup takes the lines' own when they agree (item 8).
  const [carrier, setCarrier] = useState<CarrierPick | null>(pickOf(start));
  const [carrierTouched, setCarrierTouched] = useState(false);
  const was = start?.carrier && !start.carrierCode ? start.carrier : "";
  const lineCarriers = useLineCarriers(lines, !reschedule);
  const byRef = lineCarriers.data;
  const fromLines =
    !reschedule && byRef && carriers.status === "ready"
      ? defaultCarrier(lines, byRef, carriers.options, carrierRows)
      : null;
  const preselect = fromLines?.pick ?? null;
  useEffect(() => {
    if (preselect && !carrierTouched)
      setCarrier({ carrier: preselect.name, carrierCode: preselect.code, carrierSource: preselect.source });
  }, [preselect, carrierTouched]);
  const bol = !reschedule && byRef ? bolText(lines, byRef) : "";
  // From the Appointment date cell: that day's pickups only.
  const pickups = cellDate ? allPickups.filter((p) => p.pickup.pickupDate === cellDate) : allPickups;
  const [door, setDoor] = useState(start?.door ?? "");
  const [loadRef, setLoadRef] = useState(start?.loadRef ?? "");
  const [notes, setNotes] = useState(start?.notes ?? "");
  /** Left empty, this field keeps each pickup's own value (Reschedule of several whole pickups). */
  const keepsEach = (f: RescheduleField) => reschedule?.how === "whole" && reschedule.several.includes(f);
  const placeholder = (f: RescheduleField, fallback?: string) => (keepsEach(f) ? SEVERAL : fallback);
  const [existing, setExisting] = useState(pickups[0]?.pickup.id ?? "");
  const cancel = earliestCancel(lines);
  const dateRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  const loadRefRef = useRef<HTMLInputElement>(null);
  const selectOnOpen = useSelectOnOpen();
  // A missing PICK UP # is its field's; every other blocked pickup is its date's.
  const fieldOf = (error: string) => (/PICK UP #/.test(error) ? loadRefRef : dateRef);

  const submitReschedule = async (r: RescheduleProps) => {
    const day =
      keepsEach("pickupDate") && !date.trim() ? "" : await dayOf(message, date, today, "pickup date", dateRef);
    if (day === null) return;
    const t = parseTime(time);
    if (!t.ok) {
      await message.error("Can't use this App Time", t.message);
      return refocus(timeRef);
    }
    const keep = keepsEach("carrier") && !carrier && !carrierTouched;
    const typed: RescheduleValues = {
      pickupDate: day,
      appTime: t.value,
      ...(keep ? { carrier: "", carrierCode: "", carrierSource: "" } : carrierOut(carrier, carrierTouched, was)),
      door,
      loadRef,
      notes,
    };
    const ok = await passes(message, r.check(typed), "Can't reschedule this pickup", "Reschedule anyway", fieldOf);
    if (ok) r.onReschedule(typed);
  };

  const submit = async () => {
    if (reschedule) return submitReschedule(reschedule);
    if (mode === "existing") {
      const chosen = pickups.find((p) => p.pickup.id === existing);
      if (!chosen) return message.error("Pick a pickup", "Choose the pickup these lines go on.");
      const ok = await passes(
        message,
        checkPickup({ pickupDate: chosen.pickup.pickupDate ?? "", loadRef: chosen.pickup.loadRef }, lines, today),
        "Can't add these lines to that pickup",
        "Add anyway",
        () => dateRef,
      );
      if (ok) onSchedule(chosen);
      return;
    }
    const day = await dayOf(message, date, today, "pickup date", dateRef);
    if (!day) return;
    const t = parseTime(time);
    if (!t.ok) {
      await message.error("Can't use this App Time", t.message);
      return refocus(timeRef);
    }
    const ok = await passes(
      message,
      checkPickup({ pickupDate: day, loadRef }, lines, today),
      "Can't schedule this pickup",
      "Schedule anyway",
      fieldOf,
    );
    if (ok)
      onSchedule({ input: { pickupDate: day, appTime: t.value, ...carrierOut(carrier, true, ""), door, loadRef } });
  };

  const option = (value: "new" | "existing", title: string, hint: string, body?: ReactNode) => (
    <div className={cn("rounded-lg border p-3", mode === value && "border-primary bg-primary/5")}>
      <label className="flex cursor-pointer items-start gap-2.5">
        <input type="radio" className="accent-primary mt-1" checked={mode === value} onChange={() => setMode(value)} />
        <span>
          <span className="block text-sm font-semibold">{title}</span>
          <span className="text-muted-foreground block text-xs">{hint}</span>
        </span>
      </label>
      {mode === value && body ? <div className="mt-3 pl-6">{body}</div> : null}
    </div>
  );

  return (
    <Dialog open onOpenChange={closesOnlyByHand(onClose)}>
      <DialogContent
        ref={drag.ref}
        style={drag.style}
        // The date field first, its text selected (the calendar stays closed until asked for).
        initialFocus={dateRef}
        className="sm:max-w-lg"
        data-testid="schedule-pickup-dialog"
        onKeyDown={submitOnEnter(() => void submit())}
      >
        <DialogHeader {...drag.handle}>
          <DialogTitle>
            {reschedule ? "Reschedule pickup" : "Schedule pickup"} · {lines.length} line{lines.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            {customers(lines)} · {units(lines)} units{cancel ? ` · cancel ${formatShortDay(cancel)}` : ""}.{" "}
            {!reschedule
              ? "The lines go on the Shipping schedule for that day."
              : reschedule.how === "move"
                ? "These lines move to a new pickup; the others stay on theirs."
                : "Every line on the pickup moves with it."}
          </DialogDescription>
        </DialogHeader>
        <DialogLines lines={lines} />
        <div className="space-y-2">
          {option(
            "new",
            reschedule?.how === "whole"
              ? reschedule.several.length
                ? "The pickups"
                : "The pickup"
              : reschedule
                ? "New pickup for these lines"
                : "New pickup",
            "Date, time, carrier, door and the pick up # / BOL",
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date">
                <DateField
                  enterOpens={false}
                  inputRef={dateRef}
                  autoFocus
                  aria-label="Date"
                  onFocus={selectOnOpen}
                  today={today}
                  value={date}
                  onChange={setDate}
                  placeholder={placeholder("pickupDate")}
                />
              </Field>
              <Field label="App Time">
                <Input
                  ref={timeRef}
                  placeholder={placeholder("appTime", "10:00 AM")}
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  // What will be stored, as it will show: "14:30" → "2:30 PM".
                  onBlur={() => setTime((t) => typedTimeShown(t))}
                />
              </Field>
              <Field label="Carrier">
                <CarrierField
                  value={carrier}
                  onChange={(v) => {
                    setCarrier(v);
                    setCarrierTouched(true);
                  }}
                  carriers={carriers}
                  placeholder={placeholder("carrier")}
                  was={carrierTouched ? undefined : was}
                />
                {fromLines?.hint && !carrierTouched ? (
                  <p className="text-muted-foreground mt-0.5 text-[11px]" data-testid="carrier-hint">
                    {fromLines.hint}
                  </p>
                ) : null}
              </Field>
              <Field label="Door">
                <Input
                  placeholder={placeholder("door", "e.g. 38")}
                  maxLength={20}
                  value={door}
                  onChange={(e) => setDoor(e.target.value)}
                />
              </Field>
              <Field label="PICK UP # / SHIPMENT # / BOL #" className="col-span-2">
                <Input
                  ref={loadRefRef}
                  maxLength={100}
                  placeholder={placeholder("loadRef", "optional")}
                  value={loadRef}
                  onChange={(e) => setLoadRef(e.target.value)}
                />
                {bol ? (
                  <p className="text-muted-foreground mt-0.5 text-[11px]" data-testid="a2000-bol">
                    {bol}
                  </p>
                ) : null}
              </Field>
              {reschedule ? (
                <Field label="Notes" className="col-span-2">
                  <Input
                    maxLength={500}
                    placeholder={placeholder("notes")}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Field>
              ) : null}
            </div>,
          )}
          {!reschedule &&
            option(
              "existing",
              "Add to an existing pickup",
              "Ships on the same truck",
              pickups.length ? (
                <select
                  className="h-9 w-full rounded-md border px-2 text-sm"
                  value={existing}
                  onChange={(e) => setExisting(e.target.value)}
                  aria-label="Pickup"
                >
                  {pickups.map(({ pickup: p, rows }) => (
                    <option key={p.id} value={p.id}>
                      {p.pickupDate ? formatDay(p.pickupDate) : "—"} · {timeShown(p.appTime) || "no time"} ·{" "}
                      {p.carrier || "no carrier"} · {p.loadRef || "no PICK UP #"} · {rows} line{rows === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {cellDate
                    ? `No pickup is scheduled on ${formatShortDay(cellDate)}.`
                    : "No pickup is scheduled from today on."}
                </p>
              ),
            )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()}>{reschedule ? "Reschedule" : "Schedule pickup"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Mark shipped: from a pickup, its carrier and PICK UP # (locked); off the schedule (parcel,
 * customer pickup…) it also asks the loader, trailer and door.
 */
export function MarkShippedDialog({
  lines,
  pickup,
  carriers,
  today,
  onClose,
  onShip,
}: {
  lines: LineView[];
  /** The pickup every line is on; null when none is on the schedule. */
  pickup: PickupRecord | null;
  /** The carriers of A2000 and Deposco (item 8). */
  carriers: CarrierSourceApi;
  today: string;
  onClose: () => void;
  onShip: (input: ShipInput) => void;
}) {
  const drag = useDialogDrag();
  const message = useMessage();
  const [date, setDate] = useState(formatDay(today));
  const [carrier, setCarrier] = useState<CarrierPick | null>(pickOf(pickup ?? undefined));
  const [carrierTouched, setCarrierTouched] = useState(false);
  const was = pickup?.carrier && !pickup.carrierCode ? pickup.carrier : "";
  const [loadRef, setLoadRef] = useState(pickup?.loadRef ?? "");
  const [tracking, setTracking] = useState("");
  const [loader, setLoader] = useState("");
  const [trailer, setTrailer] = useState("");
  const [door, setDoor] = useState(pickup?.door ?? "");
  const dateRef = useRef<HTMLInputElement>(null);
  const loadRefRef = useRef<HTMLInputElement>(null);
  const selectOnOpen = useSelectOnOpen();

  const submit = async () => {
    const day = await dayOf(message, date, today, "ship date", dateRef);
    if (!day) return;
    const ok = await passes(
      message,
      checkShipment({ shipDate: day, loadRef }, lines, today),
      "Can't mark shipped",
      "Mark shipped anyway",
      // A locked PICK UP # (the pickup's) is fixed on the schedule: the date is the field here.
      (error) => (/PICK UP #/.test(error) && !pickup ? loadRefRef : dateRef),
    );
    if (ok)
      onShip({ shipDate: day, ...carrierOut(carrier, carrierTouched, was), loadRef, tracking, loader, trailer, door });
  };

  return (
    <Dialog open onOpenChange={closesOnlyByHand(onClose)}>
      <DialogContent
        ref={drag.ref}
        style={drag.style}
        // The date field first, its text selected (the calendar stays closed until asked for).
        initialFocus={dateRef}
        className="sm:max-w-lg"
        data-testid="mark-shipped-dialog"
        onKeyDown={submitOnEnter(() => void submit())}
      >
        <DialogHeader {...drag.handle}>
          <DialogTitle>
            Mark {lines.length} line{lines.length === 1 ? "" : "s"} shipped
          </DialogTitle>
          <DialogDescription>
            {lines.length} line{lines.length === 1 ? "" : "s"} · {units(lines)} units.{" "}
            {pickup
              ? `On the pickup ${pickupText(pickup)}.`
              : "Not on the shipping schedule — use this when it shipped outside the normal pickup (parcel, customer pickup…)."}{" "}
            Shipped lines leave the Worklist and go to the Ship log.
          </DialogDescription>
        </DialogHeader>
        <DialogLines lines={lines} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ship date">
            <DateField
              enterOpens={false}
              inputRef={dateRef}
              autoFocus
              aria-label="Ship date"
              onFocus={selectOnOpen}
              today={today}
              value={date}
              onChange={setDate}
            />
          </Field>
          <Field label="Carrier">
            <CarrierField
              value={carrier}
              onChange={(v) => {
                setCarrier(v);
                setCarrierTouched(true);
              }}
              carriers={carriers}
              was={carrierTouched ? undefined : was}
            />
          </Field>
          <Field label="PICK UP # / SHIPMENT # / BOL #">
            <Input
              ref={loadRefRef}
              placeholder={pickup ? undefined : "optional"}
              value={loadRef}
              maxLength={100}
              disabled={!!pickup}
              title={pickup ? "The pickup's — change it on the Shipping schedule" : undefined}
              onChange={(e) => setLoadRef(e.target.value)}
            />
          </Field>
          <Field label="Tracking #">
            <Input
              placeholder="optional"
              maxLength={100}
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
            />
          </Field>
          {pickup ? null : (
            <>
              <Field label="Loader">
                <Input maxLength={100} value={loader} onChange={(e) => setLoader(e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Trailer#">
                  <Input maxLength={40} value={trailer} onChange={(e) => setTrailer(e.target.value)} />
                </Field>
                <Field label="Door">
                  <Input maxLength={20} value={door} onChange={(e) => setDoor(e.target.value)} />
                </Field>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()}>Mark shipped</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reschedule of some lines of a pickup (item 2): the whole pickup moves, or only the selected lines
 * go to a new pickup.
 */
export function RescheduleChoiceDialog({
  selected,
  onPickups,
  onClose,
  onChoose,
}: {
  /** Lines selected. */
  selected: number;
  /** Lines on the pickup(s) they are on. */
  onPickups: number;
  onClose: () => void;
  onChoose: (how: "whole" | "move") => void;
}) {
  const drag = useDialogDrag();
  return (
    <Dialog open onOpenChange={closesOnlyByHand(onClose)}>
      <DialogContent
        ref={drag.ref}
        style={drag.style}
        initialFocus={drag.ref}
        className="sm:max-w-md"
        data-testid="reschedule-choice"
      >
        <DialogHeader {...drag.handle}>
          <DialogTitle>Reschedule pickup</DialogTitle>
          <DialogDescription>
            {selected} of the {onPickups} lines on this pickup {selected === 1 ? "is" : "are"} selected. Change the
            whole pickup, or move only {selected === 1 ? "this line" : "these lines"} to a new pickup?
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Button onClick={() => onChoose("whole")}>Change the whole pickup</Button>
          <Button variant="outline" onClick={() => onChoose("move")}>
            Move only {selected === 1 ? "this line" : `these ${selected} lines`} to a new pickup
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
